import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { Div } from "../Base/index";
import { FontIcon } from "../Level1/Icon";
import Select from "components/Global/ReactSelect/TetherSelect";
import RSelect from "@sessionboard/react-select";
import Creatable from "@sessionboard/react-select/lib/Creatable";
import AsyncCreatable from "@sessionboard/react-select/lib/AsyncCreatable";
import { noop } from "lodash";
import * as R from "ramda";
import { fixFontColor } from "ui-kit/FieldTypes/utils";

const Option = props => {
  return (
    <div
      {...props.innerProps}
      style={{
        padding: "5px 0px 5px 0px",
        backgroundColor: props.isFocused ? "#FAFAFA" : "#FFFFFF",
        cursor: props.isDisabled ? "default" : "pointer"
      }}
    >
      <div style={props.getStyles("option", props)}>{props.children}</div>
    </div>
  );
};

const ArrowRenderer = ({ onMouseDown }) => (
  <Div display="row.center.center" onMouseDown={onMouseDown}>
    <FontIcon>expand_more</FontIcon>
  </Div>
);

const getSelectedValuesNames = (
  options,
  selectedValues = [],
  placeholder = "(Unknown value)"
) => {
  return selectedValues.map(value => {
    const valueToUse = value === "object" || value?.value ? value.value : value;
    const foundOption = options.find(
      o => o.value === valueToUse || o.label === valueToUse
    );
    return {
      label: R.propOr(placeholder, "label")(foundOption),
      value: valueToUse
    };
  });
};

const getSharedStaticProps = extraProps => ({
  menuPortalTarget: document.body,
  className: "basic-multi-select",
  classNamePrefix: "select",
  styles: {
    menuPortal: styles => ({
      ...styles,
      zIndex: 9999
    }),
    control: styles => ({
      ...styles,
      minHeight: 33
    }),
    dropdownIndicator: styles => ({
      ...styles,
      padding: 4
    }),
    option: (styles, { data }) => {
      const bg = data.backgroundColor || styles.backgroundColor;
      const fontColor = data.textColor || styles.color;
      const fixedFontColor = fixFontColor(bg, fontColor);
      return {
        ...styles,
        backgroundColor: bg,
        color: fixedFontColor
      };
    },
    singleValue: (styles, { data }) => {
      const bg = data.backgroundColor || styles.backgroundColor;
      const fontColor = data.textColor || styles.color;
      const fixedFontColor = fixFontColor(bg, fontColor);
      return {
        ...styles,
        backgroundColor: bg,
        color: fixedFontColor
      };
    },
    multiValue: (styles, { data }) => {
      const bg =
        data.backgroundColor ||
        data.color ||
        styles.backgroundColor ||
        "#EBEBEB";
      const fontColor = data.textColor || styles.color || "#000000";
      const fixedFontColor = fixFontColor(bg, fontColor);

      return {
        ...styles,
        backgroundColor: bg,
        color: fixedFontColor
      };
    },
    multiValueLabel: (styles, { data }) => {
      const bg =
        data.backgroundColor ||
        data.color ||
        styles.backgroundColor ||
        "#EBEBEB";
      const fontColor = data.textColor || styles.color || "#000000";
      const fixedFontColor = fixFontColor(bg, fontColor);

      return {
        ...styles,
        color: fixedFontColor
      };
    },
    ...extraProps.styles
  },
  components: extraProps.components
});

class Dropdown extends PureComponent {
  render() {
    const {
      allowMultipleSelect = false,
      isMulti = false,
      closeMenuOnSelect = true,
      clearable = true,
      onBlur = noop,
      onChange = noop,
      options = [],
      placeholder = "Select...",
      selected = [],
      value = "", // use for older dropdowns
      usePortal = noop,
      width = "100%",
      hasClearButton = false,
      useBadgeStyle = false,
      ...rest
    } = this.props;

    // @NOTE: We provide backwards compatibility here to support older ways of passing in this value:
    // If it has id + value + NOT label, we'll convert it to the new format
    const formattedOptions =
      options &&
      options[0] &&
      options[0].id &&
      options[0].value &&
      !options[0].label
        ? options.map(o => ({
            value: o.id,
            label: o.value
          }))
        : options;

    let extraProps = { components: {}, styles: {} };

    let values = [];
    const selectedValue = value || selected || [];
    if (typeof selectedValue !== "object") {
      // accept single non-object value
      values = getSelectedValuesNames(
        formattedOptions,
        [selectedValue],
        placeholder
      );
    } else {
      // accept array of objects
      values = getSelectedValuesNames(
        formattedOptions,
        selectedValue,
        placeholder
      );
    }

    // if not multiple select, get first value
    if (!allowMultipleSelect && !isMulti && !hasClearButton) {
      values = values[0];
    } else if (
      !allowMultipleSelect &&
      !isMulti &&
      values?.length > 0 &&
      hasClearButton
    ) {
      values = values[0];
    }

    if (usePortal) {
      extraProps.styles.menuPortal = styles => ({
        ...styles,
        zIndex: 9999
      });
    }

    if (width) {
      extraProps.styles.container = provided => ({
        ...provided,
        width
      });
    }

    if (useBadgeStyle) {
      extraProps.components.Option = Option;

      extraProps.styles.option = (styles, { data }) => {
        const bg = data.backgroundColor || data.color || "#EBEBEB";
        const fontColor = data.textColor || "#000000";
        const fixedFontColor = fixFontColor(bg, fontColor);

        const updatedStyles = {
          ...styles,
          backgroundColor: bg,
          color: fixedFontColor,
          width: "auto",
          display: "inline-block",
          borderRadius: "4px",
          fontSize: "12px",
          marginLeft: "12px",
          height: "100%",
          padding: "7px"
        };

        return updatedStyles;
      };

      extraProps.styles.singleValue = (styles, { data }) => {
        const bg =
          data.backgroundColor ||
          data.color ||
          styles.backgroundColor ||
          "#EBEBEB";
        const fontColor = data.textColor || styles.color || "#000000";
        const fixedFontColor = fixFontColor(bg, fontColor);

        const updatedStyles = {
          borderRadius: "4px",
          fontSize: "12px",
          padding: "7px",
          ...styles,
          backgroundColor: bg,
          color: fixedFontColor
        };

        return updatedStyles;
      };
    }

    // @NOTE: This helps with the race case on mobile w/ react-select
    const blurHandler = onBlur
      ? props => {
          setTimeout(function() {
            onBlur(props);
          }, 100);
        }
      : undefined;

    return (
      <Select
        arrowRenderer={ArrowRenderer}
        clearable={clearable}
        closeMenuOnSelect={closeMenuOnSelect}
        isMulti={isMulti || allowMultipleSelect}
        onBlur={blurHandler}
        onChange={val => {
          const formattedValue = val === null ? [] : val;
          const multipleSelect = isMulti || allowMultipleSelect;
          if (
            formattedValue?.value === "customClear" ||
            (multipleSelect &&
              R.filter(
                selectedValue => selectedValue?.value === "customClear",
                formattedValue
              ).length > 0)
          ) {
            onChange(undefined);
          } else {
            onChange(formattedValue);
          }
        }}
        options={
          hasClearButton
            ? [
                { label: "Select...", value: "customClear" },
                ...formattedOptions
              ]
            : formattedOptions
        }
        placeholder={placeholder}
        value={values}
        menuPortalTarget={usePortal ? document.body : undefined}
        {...rest}
        {...getSharedStaticProps(extraProps)}
      />
    );
  }
}

Dropdown.displayName = "Dropdown";

Dropdown.defaultProps = {
  allowMultipleSelect: false,
  clearable: false,
  placeholder: "Select...",
  selected: [],
  onBlur: undefined
};

Dropdown.propTypes = {
  allowMultipleSelect: PropTypes.bool,
  clearable: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  placeholder: PropTypes.string,
  hasClearButton: PropTypes.bool,
  selected: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    PropTypes.string,
    PropTypes.number
  ])
};

const SelectBase = ({
  isCreatable,
  isAsyncCreatable,
  isMulti,
  isClearable,
  isDisabled,
  options,
  cacheOptions,
  defaultOptions,
  loadOptions,
  onChange,
  value,
  placeholder,
  menuPlacement = "bottom",
  useBadgeStyle = false
}) => {
  let extraProps = { components: {}, styles: {} };

  if (useBadgeStyle) {
    extraProps.components.Option = Option;

    extraProps.styles.option = (styles, { data }) => {
      const bg = data.backgroundColor || data.color || "#EBEBEB";
      const fontColor = data.textColor || "#000000";
      const fixedFontColor = fixFontColor(bg, fontColor);

      const updatedStyles = {
        ...styles,
        backgroundColor: bg,
        color: fixedFontColor,
        width: "auto",
        display: "inline-block",
        borderRadius: "4px",
        fontSize: "12px",
        marginLeft: "12px",
        height: "100%",
        padding: "7px"
      };

      return updatedStyles;
    };

    extraProps.styles.singleValue = (styles, { data }) => {
      const bg =
        data.backgroundColor ||
        data.color ||
        styles.backgroundColor ||
        "#EBEBEB";
      const fontColor = data.textColor || styles.color || "#000000";
      const fixedFontColor = fixFontColor(bg, fontColor);

      const updatedStyles = {
        borderRadius: "4px",
        fontSize: "12px",
        padding: "7px",
        ...styles,
        backgroundColor: bg,
        color: fixedFontColor
      };

      return updatedStyles;
    };
  }

  let Comp = RSelect;
  if (isCreatable) {
    Comp = Creatable;
  } else if (isAsyncCreatable) {
    Comp = AsyncCreatable;
  }
  return (
    <Comp
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMulti={isMulti}
      onChange={onChange}
      options={options}
      cacheOptions={cacheOptions}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      value={value}
      placeholder={placeholder}
      menuPlacement={menuPlacement}
      {...getSharedStaticProps(extraProps)}
    />
  );
};
SelectBase.displayName = "SelectBase";
SelectBase.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
      color: PropTypes.string
    })
  )
};

const SimpleSelect = ({
  isDisabled,
  isClearable,
  onChange,
  options,
  value
}) => (
  <SelectBase
    isDisabled={isDisabled}
    isClearable={isClearable}
    onChange={({ value }) => onChange(value)}
    value={{ value, label: value }}
    options={options.map(o => ({ value: o, label: o }))}
  />
);
SimpleSelect.displayName = "SimpleSelect";

const MultiSelect = props => <SelectBase isMulti {...props} />;
MultiSelect.displayName = "MultiSelect";

const CreatableSelect = ({ onChange, onCreate, ...props }) => {
  const mode = props.isAsyncCreatable ? "isAsyncCreatable" : "isCreatable";

  const handleChange = (newValue, actionMeta) => {
    // @NOTE: this shouldn't be called because `onCreateOption` is being passed, but just in case
    if (actionMeta.action === "create-option") {
      if (Array.isArray(newValue)) {
        // if is multiple, just send the created value to onCreate
        return onCreate(newValue[newValue.length - 1].value);
      }
      return onCreate(newValue.value);
    }
    return onChange(newValue);
  };
  return (
    <SelectBase
      {...{
        onChange: handleChange,
        onCreateOption: onCreate,
        [mode]: true,
        ...props
      }}
    />
  );
};

CreatableSelect.displayName = "CreatableSelect";
CreatableSelect.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

const AsyncCreatableSelect = ({ onChange, onCreate, ...props }) => (
  <CreatableSelect
    {...{
      isAsyncCreatable: true,
      onChange,
      onCreate,
      ...props
    }}
  />
);

export {
  Dropdown,
  SimpleSelect,
  SelectBase as Select,
  CreatableSelect,
  AsyncCreatableSelect,
  MultiSelect
};
